import type { ComputedRef } from "vue";

import { computed } from "vue";

import { getEnv, getSentry } from "@solvari/utils";

declare global {
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ysa_openChat?: () => void;
  }
}

type UseYouShouldAskReturn = {
  ysaAvailable: ComputedRef<boolean>;
  ysaToggle: () => void;
};

const useYouShouldAsk = (
  websiteType?: "mainSite" | "mySolvari",
): UseYouShouldAskReturn => {
  const localeCode = getEnv().config.localeCode;
  const chatId =
    getEnv().integrations.youShouldAsk.chatId?.[localeCode]?.[
      websiteType ?? "mainSite"
    ];

  function injectYsaStyles() {
    const styleId = "ysa-custom-styles";
    if (document.querySelector(`#${styleId}`)) {
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      div.ysa_hided_popup_div.visible,
      div.ysa_status_hided.visible {
        display: none !important;
      }
    `;

    document.querySelector("head")?.append(style);
  }

  function loadChatScript() {
    if (!chatId) {
      return;
    }

    const ysaScriptUrl = `https://app.youshouldask.ai/chat/js/eS7GHe/${chatId}`;
    if (document.querySelector(`script[src="${ysaScriptUrl}"]`)) {
      return;
    }

    try {
      const script = document.createElement("script");
      script.src = ysaScriptUrl;
      script.defer = true;
      script.addEventListener("load", () => {
        injectYsaStyles();
      });

      const head = document.querySelector("head");
      if (!head) {
        console.error("Cannot find <head> element");
        return;
      }

      head.append(script);
      injectYsaStyles();
    } catch (error) {
      getSentry().captureException(
        new Error("ysa-script-load-error", { cause: error }),
      );
    }
  }

  const ysaToggle = () => {
    loadChatScript();
    window.ysa_openChat?.();
  };

  const ysaAvailable = computed(() => !!chatId);

  return Object.freeze({
    ysaToggle,
    ysaAvailable,
  });
};

export default useYouShouldAsk;
